import { useCallback, useEffect, useRef } from "react";

// ────────────────────────────────────────────────────────────────
// TIPOS
// ────────────────────────────────────────────────────────────────

export interface UseStreamBufferOptions {
	/**
	 * Velocidade de dreno em caracteres por frame (~16ms).
	 * Determina quantos caracteres são liberados do buffer a cada tick
	 * de `requestAnimationFrame`, criando uma cadência constante de texto
	 * mesmo quando tokens chegam em bursts irregulares de rede.
	 * @default 3
	 */
	charsPerFrame?: number;
	/**
	 * Callback chamado a cada flush com o conteúdo acumulado completo.
	 * É invocado no ritmo do rAF (≈60 fps), mas somente quando há
	 * conteúdo novo a ser renderizado.
	 */
	onFlush: (accumulated: string) => void;
}

// ────────────────────────────────────────────────────────────────
// HOOK
// ────────────────────────────────────────────────────────────────

/**
 * Hook de buffer de renderização para streaming de texto (RNF002).
 *
 * Funciona como uma fila FIFO entre a rede e o React:
 *   1. `push(token)` enfileira texto recebido via SSE/WS.
 *   2. Um loop interno baseado em `requestAnimationFrame` drena
 *      a fila a uma taxa constante (`charsPerFrame` por frame),
 *      interpolando bursts irregulares em uma cadência suave.
 *   3. `onFlush(accumulated)` é chamado a cada frame com o conteúdo
 *      acumulado total, pronto para ser setado no estado React.
 *
 * Métodos auxiliares:
 *   - `flush()` — drena todo o buffer de uma vez (usado quando
 *     o streaming termina para garantir que nada fique preso).
 *   - `reset()` — limpa buffer e conteúdo acumulado.
 */
export function useStreamBuffer(options: UseStreamBufferOptions) {
	const { charsPerFrame = 3, onFlush } = options;

	// ── Refs mutáveis (não disparam re-render) ──────────────────
	const queueRef = useRef(""); // texto pendente
	const accumulatedRef = useRef(""); // texto já mostrado na tela
	const rafRef = useRef<number | null>(null);
	const activeRef = useRef(false); // loop rAF está rodando?
	const isMountedRef = useRef(true);
	const onFlushRef = useRef(onFlush);
	const charsPerFrameRef = useRef(charsPerFrame);
	onFlushRef.current = onFlush;
	charsPerFrameRef.current = charsPerFrame;

	// ── Loop de rAF (interpolação constante) ────────────────────
	const tick = useCallback(() => {
		if (!isMountedRef.current) return;

		const pending = queueRef.current;
		if (pending.length === 0) {
			// Fila vazia → pausa o loop até o próximo push
			activeRef.current = false;
			return;
		}

		// Drena até `charsPerFrame` caracteres (nunca quebra um surrogate pair)
		let n = charsPerFrameRef.current;
		if (n >= pending.length) {
			accumulatedRef.current += pending;
			queueRef.current = "";
		} else {
			// Evita cortar no meio de um surrogate pair
			const code = pending.charCodeAt(n - 1);
			if (code >= 0xd800 && code <= 0xdbff) n++;
			accumulatedRef.current += pending.slice(0, n);
			queueRef.current = pending.slice(n);
		}

		onFlushRef.current(accumulatedRef.current);
		rafRef.current = requestAnimationFrame(tick);
	}, []);

	// ── Iniciar loop se parado ──────────────────────────────────
	const ensureRunning = useCallback(() => {
		if (!activeRef.current && isMountedRef.current) {
			activeRef.current = true;
			rafRef.current = requestAnimationFrame(tick);
		}
	}, [tick]);

	// ── API pública ─────────────────────────────────────────────

	/** Enfileira texto recebido do stream. */
	const push = useCallback(
		(token: string) => {
			if (!token) return;
			queueRef.current += token;
			ensureRunning();
		},
		[ensureRunning],
	);

	/** Drena todo o buffer restante de uma só vez (fim do stream / onComplete). */
	const flush = useCallback(() => {
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		activeRef.current = false;

		if (queueRef.current.length > 0) {
			accumulatedRef.current += queueRef.current;
			queueRef.current = "";
			if (isMountedRef.current) {
				onFlushRef.current(accumulatedRef.current);
			}
		}
	}, []);

	/** Limpa todo o estado (novo streaming / reset). */
	const reset = useCallback(() => {
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		activeRef.current = false;
		queueRef.current = "";
		accumulatedRef.current = "";
	}, []);

	/** Retorna o conteúdo acumulado atual (leitura ref, não dispara render). */
	const getAccumulated = useCallback(() => accumulatedRef.current, []);

	// ── Cleanup ao desmontar ────────────────────────────────────
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, []);

	return { push, flush, reset, getAccumulated };
}
